import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { IconButton } from "@/components/IconButton";
import { Link, useNavigate } from "react-router-dom";
import type { Item, ResumeItem } from "@/types/api";
import { artworkUrl } from "@/api/client";
import { itemArtworkContext, itemArtworkSrc } from "@/lib/artwork";
import { formatPlaybackPosition } from "@/lib/watchState";
import { formatReleaseDate } from "@/lib/dates";
import { formatDurationMS, formatEpisodeCode, formatSeasonDisplayLabel } from "@/lib/mediaFormat";
import { RatingBadge } from "@/components/RatingBadge";
import { WatchToggleButton } from "@/components/WatchToggleButton";
import clsx from "clsx";

export type CardShape = "portrait" | "backdrop" | "square";

interface MediaCardProps {
  item: Item;
  shape?: CardShape;
  progress?: number; // 0-100
  progressLabel?: string;
  progressTitle?: string;
  showYear?: boolean;
  watched?: boolean;
  watchUpdating?: boolean;
  onToggleWatched?: () => void;
}

interface MediaArtworkFrameProps {
  /** Artwork URL; null/empty renders the standard placeholder tile instead of a broken image. */
  imageSrc: string | null;
  imageAlt: string;
  detailsHref?: string | null;
  /** Short label for the placeholder tile (defaults to the first character of imageAlt). */
  fallbackLabel?: string;
  playLabel: string;
  onPlay: (event: MouseEvent<HTMLButtonElement>) => void;
  playDisabled?: boolean;
  shape?: CardShape;
  ratingItem?: Item | null;
  progress?: number;
  progressLabel?: string;
  progressTitle?: string;
  overlayAction?: ReactNode;
  children?: ReactNode;
}

export function MediaArtworkFrame({
  imageSrc,
  imageAlt,
  detailsHref,
  fallbackLabel,
  playLabel,
  onPlay,
  playDisabled = false,
  shape,
  ratingItem,
  progress,
  progressLabel,
  progressTitle,
  overlayAction,
  children,
}: MediaArtworkFrameProps) {
  const normalizedProgress = progress === undefined ? undefined : Math.max(0, Math.min(100, progress));
  const showProgress = normalizedProgress !== undefined && normalizedProgress > 0;
  const artwork = (
    <>
      {imageSrc ? (
        <img
          className="media-card-image"
          src={imageSrc}
          alt={imageAlt}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="media-card-image media-card-image--placeholder" aria-hidden="true">
          <span>{fallbackLabel ?? imageAlt.charAt(0).toUpperCase()}</span>
        </div>
      )}
      {children}
    </>
  );
  return (
    <div className={clsx("media-card-image-wrap", shape && `media-card-image-wrap--${shape}`)}>
      {detailsHref ? (
        <Link className="media-card-artwork-link" to={detailsHref} aria-label={`View ${imageAlt} details`}>
          {artwork}
        </Link>
      ) : (
        artwork
      )}
      <button
        type="button"
        className="media-card-play-overlay"
        onClick={onPlay}
        aria-label={playLabel}
        disabled={playDisabled}
      >
        ▶
      </button>
      {ratingItem && <RatingBadge item={ratingItem} placement="card" />}
      {overlayAction}
      {showProgress && progressLabel && (
        <span className="media-card-progress-label" title={progressTitle ?? progressLabel}>
          {progressLabel}
        </span>
      )}
      {showProgress && (
        <div
          className="media-card-progress"
          role="progressbar"
          aria-label={progressLabel ? `${progressLabel} watch progress` : "Watch progress"}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(normalizedProgress)}
        >
          <div className="media-card-progress-fill" style={{ width: `${normalizedProgress}%` }} />
        </div>
      )}
    </div>
  );
}

/**
 * Media card used in grids and scroll rows.
 * Shape adapts to content type (portrait for movies/tv, square for music, backdrop for episodes).
 */
export function MediaCard({
  item,
  shape,
  progress,
  progressLabel,
  progressTitle,
  showYear = true,
  watched = false,
  watchUpdating = false,
  onToggleWatched,
}: MediaCardProps) {
  const { attributes: attrs } = item;

  const resolvedShape = shape ?? inferShape(attrs.item_type);
  const artworkType = resolvedShape === "backdrop" ? "backdrop" : "poster";
  const imgWidth = resolvedShape === "backdrop" ? 400 : 200;
  const cardTitle = formatCardTitle(item);
  const cardMeta = formatCardMeta(item, showYear);
  const artworkContext = itemArtworkContext(item);
  const itemHref = buildItemHref(item, artworkContext);
  const autoplayHref = buildItemHref(item, artworkContext, { autoplay: true });
  const seriesHref = attrs.item_type === "episode" && attrs.series_id ? `/items/${attrs.series_id}` : null;
  const seasonHref = attrs.item_type === "episode" && attrs.season_id ? `/items/${attrs.season_id}` : null;
  const seasonLabel = attrs.item_type === "episode" ? seasonLinkLabel(attrs) : null;
  const episodeCode = attrs.item_type === "episode" ? formatEpisodeCode(item) : null;
  const episodeTitle = attrs.item_type === "episode" ? (attrs.episode_title || attrs.title) : null;
  const handlePlay = useMediaCardPlay(autoplayHref);

  return (
    <article className={clsx("media-card", `media-card--${resolvedShape}`)}>
      <MediaArtworkFrame
        imageSrc={itemArtworkSrc(item, artworkType, { width: imgWidth, format: "webp", context: artworkContext })}
        imageAlt={attrs.title}
        detailsHref={itemHref}
        playLabel={`Play ${cardTitle}`}
        onPlay={handlePlay}
        ratingItem={item}
        progress={progress}
        progressLabel={progressLabel}
        progressTitle={progressTitle}
        overlayAction={onToggleWatched && (
          <WatchToggleButton
            cardOverlay
            wrapperClassName="media-card-watch-toggle-wrap"
            className={clsx("media-card-watch-toggle", {
              "media-card-watch-toggle--watched": watched,
              "media-card-watch-toggle--pending": watchUpdating,
            })}
            watched={watched}
            updating={watchUpdating}
            watchedLabel="Mark as watched"
            unwatchedLabel="Mark as unwatched"
            watchedAccessibleName={`Mark ${cardTitle} as watched`}
            unwatchedAccessibleName={`Mark ${cardTitle} as unwatched`}
            onToggle={onToggleWatched}
          />
        )}
      />
      <div className="media-card-info">
        {attrs.item_type === "episode" ? (
          <>
            {seriesHref ? (
              <Link
                className="media-card-title media-card-link"
                to={seriesHref}
                onClick={stopCardLinkClick}
                onKeyDown={stopCardLinkKeyDown}
              >
                {cardTitle}
              </Link>
            ) : (
              <span className="media-card-title">{cardTitle}</span>
            )}
            <EpisodeMetaLinks
              itemHref={itemHref}
              seasonHref={seasonHref}
              seasonLabel={seasonLabel}
              episodeCode={episodeCode}
              episodeTitle={episodeTitle}
              secondary={cardMeta.secondary}
            />
          </>
        ) : (
          <>
            <Link className="media-card-title media-card-link" to={itemHref}>
              {cardTitle}
            </Link>
            {cardMeta.primary && (
              <span className="media-card-meta">
                {cardMeta.primary}
              </span>
            )}
            {cardMeta.secondary && (
              <span className="media-card-meta media-card-meta--secondary">
                {cardMeta.secondary}
              </span>
            )}
          </>
        )}
      </div>
    </article>
  );
}

function buildItemHref(item: Item, context?: string, options?: { autoplay?: boolean }): string {
  const params = new URLSearchParams();
  if (options?.autoplay) {
    params.set("autoplay", "1");
  }
  if (context) {
    params.set("context", context);
  }
  const query = params.toString();
  return query ? `/items/${item.id}?${query}` : `/items/${item.id}`;
}

function inferShape(itemType: string): CardShape {
  switch (itemType) {
    case "episode":
      return "backdrop";
    case "track":
      return "square";
    default:
      return "portrait";
  }
}

// "Season 3 · Villains / Fugitives" when the season has a name of its own, plain
// "Season 3" otherwise, and null when the metadata identifies no season at all.
function seasonLinkLabel(
  attrs: Pick<Item["attributes"], "season_number" | "season_title">,
): string | null {
  if (!attrs.season_title && attrs.season_number == null) return null;
  return formatSeasonDisplayLabel(attrs.season_number, attrs.season_title);
}

function formatCardTitle(item: Item): string {
  const attrs = item.attributes;
  if (attrs.item_type === "episode" && attrs.series_title) {
    return attrs.series_title;
  }
  return attrs.title;
}

function formatCardMeta(item: Item, showYear: boolean): { primary: string | null; secondary: string | null } {
  const attrs = item.attributes;
  const releaseDate = formatReleaseDate(attrs.release_date);

  switch (attrs.item_type) {
    case "show":
      return {
        primary: "Series",
        secondary: releaseDate ?? (showYear && attrs.year ? String(attrs.year) : null),
      };
    case "season":
      return {
        primary: seasonLinkLabel(attrs) ?? "Season",
        secondary: releaseDate ?? (showYear && attrs.year ? String(attrs.year) : null),
      };
    case "episode": {
      const code = formatEpisodeCode(item);
      const episodeTitle = attrs.episode_title || attrs.title;
      let primary: string | null = null;
      if (episodeTitle && code) primary = `${episodeTitle} • ${code}`;
      else if (episodeTitle) primary = episodeTitle;
      else if (code) primary = code;
      return {
        primary: primary ?? "Episode",
        secondary: releaseDate ?? formatDurationMS(attrs.duration_ms),
      };
    }
    case "track":
      return {
        primary: "Track",
        secondary: releaseDate ?? formatDurationMS(attrs.duration_ms),
      };
    case "photo":
      return {
        primary: "Photo",
        secondary: releaseDate ?? (showYear && attrs.year ? String(attrs.year) : null),
      };
    default:
      return {
        primary: showYear && attrs.year ? String(attrs.year) : formatDurationMS(attrs.duration_ms) ?? attrs.item_type,
        secondary: releaseDate,
      };
  }
}

/** Card for continue watching / on deck rows */
interface ContinueCardProps {
  resumeItem: ResumeItem;
  removeUpdating?: boolean;
  onRemoveFromContinueWatching?: (itemId: string) => void;
}

export function ContinueCard({
  resumeItem,
  removeUpdating = false,
  onRemoveFromContinueWatching,
}: ContinueCardProps) {
  const itemRef = resumeItem.relationships.item.data;
  const embeddedItem = "attributes" in itemRef ? (itemRef as Item) : null;
  const nextUp = resumeItem.attributes.next_up === true;
  const title = formatContinueTitle(embeddedItem) ?? itemRef.id;
  const subtitle = nextUp
    ? "Start watching"
    : formatContinueSubtitle(embeddedItem, resumeItem.attributes.position_ms);
  const progress = Math.max(0, Math.min(100, resumeItem.attributes.progress_percent ?? 0));
  const itemHref = `/items/${itemRef.id}`;
  const autoplayHref = `/items/${itemRef.id}?autoplay=1`;
  const artworkSrc = embeddedItem
    ? itemArtworkSrc(embeddedItem, "backdrop", { width: 400, format: "webp" })
    : undefined;
  const seriesHref = embeddedItem?.attributes.series_id ? `/items/${embeddedItem.attributes.series_id}` : null;
  const seasonHref = embeddedItem?.attributes.season_id ? `/items/${embeddedItem.attributes.season_id}` : null;
  const seasonLabel = embeddedItem ? seasonLinkLabel(embeddedItem.attributes) : null;
  const episodeCode = embeddedItem ? formatEpisodeCode(embeddedItem) : null;
  const episodeTitle = embeddedItem?.attributes.item_type === "episode"
    ? (embeddedItem.attributes.episode_title || embeddedItem.attributes.title)
    : null;
  const handlePlay = useMediaCardPlay(autoplayHref);

  function handleRemoveFromContinueWatching(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onRemoveFromContinueWatching?.(itemRef.id);
  }

  return (
    <article className="media-card media-card--backdrop">
      <MediaArtworkFrame
        imageSrc={artworkSrc ?? artworkUrl(itemRef.id, "backdrop", { width: 400, format: "webp" })}
        imageAlt={title}
        detailsHref={itemHref}
        playLabel={`Play ${title}`}
        onPlay={handlePlay}
        ratingItem={embeddedItem}
        progress={progress}
        overlayAction={onRemoveFromContinueWatching && (
          <IconButton
            type="button"
            wrapperClassName="media-card-watch-toggle-wrap"
            className={clsx("media-card-watch-toggle", {
              "media-card-watch-toggle--pending": removeUpdating,
            })}
            label="Remove from Continue Watching"
            accessibleName={`Remove ${title} from Continue Watching`}
            onClick={handleRemoveFromContinueWatching}
            disabled={removeUpdating}
          >
            ✓
          </IconButton>
        )}
      >
        {/* Zero-progress next-up entries reuse the artwork label slot the
            progress label occupies for in-progress entries (it self-hides at
            0%), so the indicator follows the existing overlay pattern. */}
        {nextUp && (
          <span className="media-card-progress-label" title={`Next up: ${title}`}>
            Next Up
          </span>
        )}
      </MediaArtworkFrame>
      <div className="media-card-info">
        {seriesHref ? (
          <Link
            className="media-card-title media-card-link"
            to={seriesHref}
            onClick={stopCardLinkClick}
            onKeyDown={stopCardLinkKeyDown}
          >
            {title}
          </Link>
        ) : (
          <Link className="media-card-title media-card-link" to={itemHref}>
            {title}
          </Link>
        )}
        {embeddedItem?.attributes.item_type === "episode" ? (
          <EpisodeMetaLinks
            itemHref={itemHref}
            seasonHref={seasonHref}
            seasonLabel={seasonLabel}
            episodeCode={episodeCode}
            episodeTitle={episodeTitle}
            secondary={subtitle}
          />
        ) : (
          <span className="media-card-meta">{subtitle}</span>
        )}
      </div>
    </article>
  );
}

function useMediaCardPlay(autoplayHref: string) {
  const navigate = useNavigate();

  function handlePlay(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    navigate(autoplayHref);
  }

  return handlePlay;
}

interface EpisodeMetaLinksProps {
  itemHref: string;
  seasonHref: string | null;
  seasonLabel: string | null;
  episodeCode: string | null;
  episodeTitle: string | null;
  secondary?: ReactNode;
}

function EpisodeMetaLinks({
  itemHref,
  seasonHref,
  seasonLabel,
  episodeCode,
  episodeTitle,
  secondary,
}: EpisodeMetaLinksProps) {
  return (
    <>
      <div className="media-card-meta media-card-meta--wrap">
        {seasonLabel && (
          seasonHref ? (
            <Link
              className="media-card-link"
              to={seasonHref}
              onClick={stopCardLinkClick}
              onKeyDown={stopCardLinkKeyDown}
            >
              {seasonLabel}
            </Link>
          ) : (
            <span>{seasonLabel}</span>
          )
        )}
        {episodeCode && (
          <>
            {seasonLabel && <span className="media-card-meta-separator">•</span>}
            <Link
              className="media-card-link"
              to={itemHref}
              onClick={stopCardLinkClick}
              onKeyDown={stopCardLinkKeyDown}
            >
              {episodeCode}
            </Link>
          </>
        )}
        {episodeTitle && (
          <>
            {(seasonLabel || episodeCode) && <span className="media-card-meta-separator">•</span>}
            <Link
              className="media-card-link"
              to={itemHref}
              onClick={stopCardLinkClick}
              onKeyDown={stopCardLinkKeyDown}
            >
              {episodeTitle}
            </Link>
          </>
        )}
      </div>
      {secondary && <span className="media-card-meta media-card-meta--secondary">{secondary}</span>}
    </>
  );
}

function formatContinueTitle(item: Item | null): string | null {
  if (!item) return null;
  const attrs = item.attributes;
  if (attrs.item_type === "episode" && attrs.series_title) {
    return attrs.series_title;
  }
  return attrs.title;
}

function formatContinueSubtitle(item: Item | null, positionMS: number): string {
  const resume = `Resume from ${formatPlaybackPosition(positionMS)}`;
  if (!item) return resume;

  const attrs = item.attributes;
  if (attrs.item_type === "episode") return resume;

  return resume;
}

function stopCardLinkClick(event: MouseEvent<HTMLAnchorElement>) {
  event.stopPropagation();
}

function stopCardLinkKeyDown(event: KeyboardEvent<HTMLAnchorElement>) {
  event.stopPropagation();
}
